import {
  fetchGeoDataFilesByCsib,
  getFromBase64Csib,
  type CoordinatesClient,
} from '@data-access/coordinates';
import { createLonLatToSceneConverter } from '@trimble/web3d-providers';
import { Point2D } from '@trimble/utilities';
import { getLogger } from '@trimble/logging';

export interface ProjectGeoReference {
  csib: string;
  center: Point2D;
}

export interface AxisAlignedBounds {
  min: Point2D;
  max: Point2D;
}

export interface ResolvedProjectGeo {
  geo: ProjectGeoReference;
  bounds?: AxisAlignedBounds;
}

interface LonLat {
  lon: number;
  lat: number;
}

export function hasProjectCsib64(crs?: {
  name?: string;
  csib64?: string;
}): boolean {
  return !!crs?.csib64?.trim();
}

/**
 * Parses Connect project boundary geometry: array of [lat, lon] coordinates.
 */
export function parseProjectBoundaryGeometry(
  geometry?: string
): LonLat[] | undefined {
  if (!geometry) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(geometry) as number[][];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return undefined;
    }
    return parsed.map((coord) => ({ lat: coord[0], lon: coord[1] }));
  } catch {
    getLogger().warn('Failed to parse project boundary geometry');
    return undefined;
  }
}

function centerFromProjectedPoints(points: Point2D[]): Point2D | undefined {
  if (points.length === 0) {
    return undefined;
  }

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));
  const HALF = 2;

  return {
    x: (minX + maxX) / HALF,
    y: (minY + maxY) / HALF,
  };
}

export function boundsFromProjectedPoints(
  points: Point2D[]
): AxisAlignedBounds | undefined {
  const minPointsForBounds = 2;
  if (points.length < minPointsForBounds) {
    return undefined;
  }

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));
  if (minX === maxX || minY === maxY) {
    return undefined;
  }

  return {
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY },
  };
}

function toOptionalNumber(
  value: number | string | null | undefined
): number | undefined {
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return undefined;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function extentsCenterLonLat(extents: {
  minLat?: number | string;
  minLon?: number | string;
  maxLat?: number | string;
  maxLon?: number | string;
}): LonLat | undefined {
  const minLat = toOptionalNumber(extents.minLat);
  const minLon = toOptionalNumber(extents.minLon);
  const maxLat = toOptionalNumber(extents.maxLat);
  const maxLon = toOptionalNumber(extents.maxLon);
  if (
    minLat === undefined ||
    minLon === undefined ||
    maxLat === undefined ||
    maxLon === undefined
  ) {
    return undefined;
  }

  const HALF = 2;
  return {
    lat: (minLat + maxLat) / HALF,
    lon: (minLon + maxLon) / HALF,
  };
}

async function projectLonLatPoints(
  csib64: string,
  lonLatPoints: LonLat[],
  coordinatesClient: CoordinatesClient
): Promise<Point2D[] | undefined> {
  const geoDataFiles = await fetchGeoDataFilesByCsib(coordinatesClient, csib64);
  const converter = await createLonLatToSceneConverter(csib64, geoDataFiles);

  const projected = lonLatPoints
    .map((point) => converter.lonLatToScene(point.lon, point.lat))
    .filter((grid): grid is NonNullable<typeof grid> => grid !== null)
    .map((grid) => ({ x: grid.x, y: grid.y }));

  return projected.length > 0 ? projected : undefined;
}

/**
 * Resolves project-level geo-reference (CSIB + center in project CRS) for 2D basemap
 * before any point cloud is loaded.
 */
export async function resolveProjectGeoReference(
  csib64: string,
  geometry: string | undefined,
  coordinatesClient: CoordinatesClient
): Promise<ResolvedProjectGeo | undefined> {
  const trimmedCsib = csib64.trim();
  if (!trimmedCsib) {
    return undefined;
  }

  try {
    const boundaryLonLat = parseProjectBoundaryGeometry(geometry);

    if (boundaryLonLat?.length) {
      const projected = await projectLonLatPoints(
        trimmedCsib,
        boundaryLonLat,
        coordinatesClient
      );
      const center = projected
        ? centerFromProjectedPoints(projected)
        : undefined;
      if (center === undefined) {
        return undefined;
      }
      return {
        geo: { csib: trimmedCsib, center },
        bounds: projected ? boundsFromProjectedPoints(projected) : undefined,
      };
    }

    const fromBase64 = await getFromBase64Csib({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query: { csib_64: trimmedCsib },
      client: coordinatesClient,
    });

    if (fromBase64.error) {
      throw new Error(JSON.stringify(fromBase64.error));
    }

    const zoneInfo = fromBase64.data?.coordinateSystem?.zoneInfo;
    const extentsCenter = zoneInfo?.extents
      ? extentsCenterLonLat(zoneInfo.extents)
      : undefined;
    if (extentsCenter) {
      const projected = await projectLonLatPoints(
        trimmedCsib,
        [extentsCenter],
        coordinatesClient
      );
      const center = projected
        ? centerFromProjectedPoints(projected)
        : undefined;
      if (center !== undefined) {
        return { geo: { csib: trimmedCsib, center } };
      }
    }

    // originEast/originNorth are often false easting/northing (e.g. UTM 500000, 0).
    const originEast = toOptionalNumber(zoneInfo?.originEast);
    const originNorth = toOptionalNumber(zoneInfo?.originNorth);
    if (originEast !== undefined && originNorth !== undefined) {
      return {
        geo: {
          csib: trimmedCsib,
          center: { x: originEast, y: originNorth },
        },
      };
    }

    return undefined;
  } catch (error) {
    getLogger().error('Failed to resolve project geo-reference', error);
    return undefined;
  }
}
